import apiClient from './api';

// Configuration and settings routes for the admin pages (docs/design/contracts.md §7, §13, §14, §30).
const data = (promise) => promise.then((response) => response.data);

export const IMPORT_ENTITIES = ['hierarchy-nodes', 'communities', 'mappings', 'directory'];

const usage = (days) => ({ params: { days: days || 30 } });

const DIRECTORY_PAGE_SIZE = 200;

async function allDirectoryEntries() {
  const items = [];
  for (let page = 1; ; page += 1) {
    const result = await data(apiClient.get('/config/directory', { params: { page, pageSize: DIRECTORY_PAGE_SIZE } }));
    items.push(...(result?.items || []));
    if (!result?.pagination?.hasNextPage) return items;
  }
}

export const adminAPI = {
  getLevels: () => data(apiClient.get('/config/hierarchy/levels')),
  saveLevels: (levels) => data(apiClient.put('/config/hierarchy/levels', levels)),
  insertLevelAbove: (request) => data(apiClient.post('/config/hierarchy/levels/insert-above', request)),
  getNodes: () => data(apiClient.get('/config/hierarchy/nodes')),
  createNode: (node) => data(apiClient.post('/config/hierarchy/nodes', node)),
  updateNode: (node) => data(apiClient.put(`/config/hierarchy/nodes/${encodeURIComponent(node.id)}`, node)),
  moveNode: (id, parentId) => data(apiClient.post(`/config/hierarchy/nodes/${encodeURIComponent(id)}/move`, { parentId })),
  deleteNode: (id) => data(apiClient.delete(`/config/hierarchy/nodes/${encodeURIComponent(id)}`)),

  getCommunities: () => data(apiClient.get('/config/communities', { params: { includeInactive: true } })),
  createCommunity: (community) => data(apiClient.post('/config/communities', community)),
  updateCommunity: (community) => data(apiClient.put(`/config/communities/${encodeURIComponent(community.id)}`, community)),
  getCommunityStats: (days) => data(apiClient.get('/config/communities/stats', usage(days))),

  getMappings: () => data(apiClient.get('/config/mappings')),
  createMapping: (mapping) => data(apiClient.post('/config/mappings', mapping)),
  updateMapping: (mapping) => data(apiClient.put(`/config/mappings/${encodeURIComponent(mapping.id)}`, mapping)),
  getRoutingEntities: (days) => data(apiClient.get('/config/routing-entities', usage(days))),

  getDirectory: allDirectoryEntries,
  createDirectoryEntry: (entry) => data(apiClient.post('/config/directory', entry)),
  updateDirectoryEntry: (entry) => data(apiClient.put(`/config/directory/${encodeURIComponent(entry.id)}`, entry)),
  deleteDirectoryEntry: (id) => data(apiClient.delete(`/config/directory/${encodeURIComponent(id)}`)),
  getDirectoryInternal: () => data(apiClient.get('/config/directory/internal')),

  /** Preview by default (the server's dryRun defaults to true); apply only with dryRun === false. */
  importCsv: (entity, file, { dryRun = true } = {}) => {
    if (!IMPORT_ENTITIES.includes(entity)) throw new Error(`Unknown import entity ${entity}`);
    const form = new FormData();
    form.append('file', file);
    return data(
      apiClient.post(`/config/${entity}/import`, form, {
        params: dryRun ? undefined : { dryRun: false },
        headers: { 'Content-Type': 'multipart/form-data' },
      })
    );
  },
  getTemplate: (entity) => data(apiClient.get(`/config/${entity}/template`, { responseType: 'text' })),
  seedLeland: ({ dryRun = true } = {}) => data(apiClient.post('/config/seed/leland', null, { params: dryRun ? undefined : { dryRun: false } })),

  getTaxonomyVersions: () => data(apiClient.get('/config/taxonomy')),
  createTaxonomyDraft: (request) => data(apiClient.post('/config/taxonomy', request)),
  updateTaxonomyDraft: (version) => data(apiClient.put(`/config/taxonomy/${encodeURIComponent(version.id)}`, version)),
  activateTaxonomy: (id) => data(apiClient.post(`/config/taxonomy/${encodeURIComponent(id)}/activate`)),
  getTaxonomyCounts: (days) => data(apiClient.get('/config/taxonomy/counts', usage(days))),
  getReanalysisEstimate: (days) => data(apiClient.get('/config/taxonomy/reanalysis-estimate', usage(days))),
  reanalyzeCalls: (days, confirmCallCount) => data(apiClient.post('/config/taxonomy/reanalyze', null, { params: { days: days || 31, confirmCallCount } })),

  getUsers: () => data(apiClient.get('/config/users')),
  createUser: (request) => data(apiClient.post('/config/users', request)),
  setUserRoles: (id, roles) => data(apiClient.put(`/config/users/${encodeURIComponent(id)}/roles`, { roles })),
  setUserScope: (id, scope) => data(apiClient.put(`/config/users/${encodeURIComponent(id)}/scope`, scope)),
  deactivateUser: (id) => data(apiClient.delete(`/config/users/${encodeURIComponent(id)}`)),

  getAiSettings: () => data(apiClient.get('/settings/ai')),
  saveAiSettings: (settings) => data(apiClient.put('/settings/ai', settings)),
  getAiStatus: () => data(apiClient.get('/settings/ai/status')),
  deleteSyntheticContent: () => data(apiClient.delete('/settings/ai/synthetic-content')),
  getSlaSettings: () => data(apiClient.get('/settings/sla')),
  saveSlaSettings: (settings) => data(apiClient.put('/settings/sla', settings)),
  getBusinessHours: () => data(apiClient.get('/settings/business-hours')),
  saveBusinessHours: (hours) => data(apiClient.put('/settings/business-hours', hours)),
  getAgentPhotos: () => data(apiClient.get('/settings/agent-photos')),
  setAgentPhotos: (enabled) => data(apiClient.put('/settings/agent-photos', { enabled })),
  getAgentPhotosConsentLink: () => data(apiClient.post('/settings/agent-photos/consent-link')),
  setPersonUsesInitials: (userId, useInitials) => data(apiClient.put(`/settings/agent-photos/people/${encodeURIComponent(userId)}`, { useInitials })),
  getAttributionSettings: () => data(apiClient.get('/settings/attribution')),
  saveAttributionSettings: (settings) => data(apiClient.put('/settings/attribution', settings)),
  getTerminology: () => data(apiClient.get('/settings/terminology')),
  saveTerminology: (terminology) => data(apiClient.put('/settings/terminology', terminology)),
};

/** The server's `{ message }` for 400/404/409, else a generic line. */
export const errorMessage = (error, fallback = 'Something went wrong. Try again.') =>
  error?.response?.data?.message || (typeof error?.response?.data === 'string' && error.response.data) || fallback;

export default adminAPI;
